import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatApiError } from "@/lib/api";
import { Plus, Search, ArrowUpRight, X, Download } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({ name: "", code: "", phone: "", email: "", address: "", opening_balance: 0, notes: "" });
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [exporting, setExporting] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get("/customers");
            setRows(r.data);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
    }, []);

    async function createCustomer(e) {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/customers", { ...form, opening_balance: Number(form.opening_balance || 0) });
            toast.success("Müşteri eklendi");
            setShowModal(false);
            setForm({ name: "", code: "", phone: "", email: "", address: "", opening_balance: 0, notes: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    const filtered = rows.filter(
        (r) =>
            !q ||
            r.name?.toLowerCase().includes(q.toLowerCase()) ||
            r.code?.toLowerCase().includes(q.toLowerCase()) ||
            r.phone?.toLowerCase().includes(q.toLowerCase()),
    );

    function toggleAll(check) {
        if (check) setSelected(new Set(filtered.map((r) => r.id)));
        else setSelected(new Set());
    }

    function toggleOne(id) {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    }

    async function exportSelected() {
        if (selected.size === 0) {
            toast.error("Müşteri seçin");
            return;
        }
        setExporting(true);
        try {
            const res = await api.post(
                "/customers/statements/bulk.xlsx",
                { customer_ids: Array.from(selected) },
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "cari_hesap_ekstresi.xlsx";
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success(`${selected.size} müşteri Excel'e aktarıldı`);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "İndirme başarısız");
        } finally {
            setExporting(false);
        }
    }

    const allSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));

    return (
        <div>
            <header className="border-b border-black/10 px-10 py-6 flex items-center justify-between">
                <div>
                    <div className="overline">Bölüm 02</div>
                    <h1 className="font-display text-4xl font-black tracking-tight leading-none mt-1">Müşteriler</h1>
                </div>
                <div className="flex items-center gap-2">
                    {selected.size > 0 && (
                        <button
                            data-testid="bulk-export"
                            onClick={exportSelected}
                            disabled={exporting}
                            className="btn-outline"
                        >
                            <Download size={13} /> {exporting ? "…" : `Seçilenleri Excel'e Aktar (${selected.size})`}
                        </button>
                    )}
                    <button
                        data-testid="new-customer-button"
                        onClick={() => setShowModal(true)}
                        className="btn-sharp"
                    >
                        <Plus size={14} /> Yeni Müşteri
                    </button>
                </div>
            </header>

            <div className="px-10 py-6 border-b border-black/10 flex items-center gap-3">
                <Search size={16} className="text-neutral-500" />
                <input
                    data-testid="customer-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Ada, koda veya telefona göre ara…"
                    className="w-full border border-black/15 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue"
                />
            </div>

            <div className="px-10 py-6">
                {loading ? (
                    <div className="font-mono text-xs uppercase text-neutral-500">Yükleniyor…</div>
                ) : filtered.length === 0 ? (
                    <div className="text-sm text-neutral-500 font-mono">Kayıtlı müşteri yok.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="customers-table">
                        <thead>
                            <tr className="border-b border-black/10">
                                <th className="w-8 py-3">
                                    <input
                                        data-testid="select-all"
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={(e) => toggleAll(e.target.checked)}
                                        className="accent-brand-blue"
                                    />
                                </th>
                                <th className="text-left overline py-3 pr-4">Müşteri</th>
                                <th className="text-left overline py-3 pr-4">Kod</th>
                                <th className="text-left overline py-3 pr-4">Telefon</th>
                                <th className="text-right overline py-3 pr-4">Fatura (₺)</th>
                                <th className="text-right overline py-3 pr-4">Tahsilat (₺)</th>
                                <th className="text-right overline py-3 pr-4">Bakiye (₺)</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c) => (
                                <tr key={c.id} className={`border-b border-black/5 ${selected.has(c.id) ? "bg-blue-50/40" : ""}`}>
                                    <td className="py-3">
                                        <input
                                            data-testid={`select-${c.id}`}
                                            type="checkbox"
                                            checked={selected.has(c.id)}
                                            onChange={() => toggleOne(c.id)}
                                            className="accent-brand-blue"
                                        />
                                    </td>
                                    <td className="py-3 pr-4 font-medium">{c.name}</td>
                                    <td className="py-3 pr-4 font-mono text-xs text-neutral-500">{c.code || "-"}</td>
                                    <td className="py-3 pr-4 font-mono text-xs">{c.phone || "-"}</td>
                                    <td className="py-3 pr-4 text-right font-mono-num">{formatMoney(c.invoiced)}</td>
                                    <td className="py-3 pr-4 text-right font-mono-num text-brand-green">
                                        {formatMoney(c.collected)}
                                    </td>
                                    <td
                                        className={`py-3 pr-4 text-right font-mono-num font-semibold ${
                                            c.balance > 0 ? "text-brand-red" : c.balance < 0 ? "text-brand-blue" : ""
                                        }`}
                                    >
                                        {formatMoney(c.balance)}
                                    </td>
                                    <td className="py-3 text-right">
                                        <Link
                                            to={`/musteriler/${c.id}`}
                                            data-testid={`open-customer-${c.id}`}
                                            className="text-brand-blue hover:underline inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest"
                                        >
                                            Aç <ArrowUpRight size={14} />
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {showModal && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
                    onClick={() => setShowModal(false)}
                >
                    <form
                        data-testid="new-customer-form"
                        onSubmit={createCustomer}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white border border-black/15 w-full max-w-xl p-8 animate-fade-up"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-display text-2xl font-bold tracking-tight">Yeni Müşteri</h2>
                            <button type="button" onClick={() => setShowModal(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Ad" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="cf-name" />
                            <Field label="Kod" value={form.code} onChange={(v) => setForm({ ...form, code: v })} testid="cf-code" />
                            <Field label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="cf-phone" />
                            <Field label="E-posta" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="cf-email" />
                            <Field
                                label="Açılış Bakiyesi (₺)"
                                type="number"
                                value={form.opening_balance}
                                onChange={(v) => setForm({ ...form, opening_balance: v })}
                                testid="cf-opening"
                            />
                            <div className="col-span-2">
                                <label>
                                    <span className="overline block mb-2">Adres</span>
                                    <textarea
                                        data-testid="cf-address"
                                        value={form.address}
                                        onChange={(e) => setForm({ ...form, address: e.target.value })}
                                        className="w-full border border-black/20 px-3 py-2 text-sm font-mono h-20 focus:outline-none focus:border-brand-blue"
                                    />
                                </label>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button type="button" onClick={() => setShowModal(false)} className="btn-outline">
                                Vazgeç
                            </button>
                            <button data-testid="cf-submit" disabled={saving} className="btn-sharp">
                                {saving ? "Kaydediliyor…" : "Kaydet"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
    return (
        <label className="block">
            <span className="overline block mb-2">
                {label}
                {required && <span className="text-brand-red"> *</span>}
            </span>
            <input
                data-testid={testid}
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue"
            />
        </label>
    );
}
